import { carrierName } from '../console/carriers.ts';
import { field, loadMore, pill, quoteRef } from '../console/components.ts';
import {
  agentName,
  date,
  esc,
  fmt,
  icon,
  lineName,
  type Money,
  PRODUCT,
  q,
  when,
} from '../console/format.ts';
import { selectControl, textControl } from '../controls.ts';

/**
 * The complete quote in «Cotizar» (#314, ADR-0040): each option's coverages and premium broken
 * down, options compared side by side, the client's proposal (PDF, WhatsApp or e-mail with
 * consent), closing a quote as lost, two versions compared, and the list searched and filtered
 * with each quote's status, origin and validity. Pure rendering: cotizar.ts loads and acts.
 */

export type Premium = {
  net: Money;
  issuanceFee: Money;
  installmentSurcharge: Money;
  vat: Money;
  gross: Money;
  installments: { count: number; first: Money; regular: Money; last: Money };
};
export type CoverageView = {
  code: string;
  carrierCode: string;
  name: string;
  kind: string | null;
  limit: Money | null;
  limitText: string | null;
  deductible: string | null;
  coinsurance: string | null;
  simulated: boolean;
};
export type OptionDetail = {
  option: string;
  planName: string;
  decision: string;
  premium: Premium | null;
  commission: Money | null;
  coverages: CoverageView[];
};
export type CarrierDetail = {
  carrier: string;
  name: string;
  carrierQuoteRef: string | null;
  tariffVersion: string | null;
  firm: boolean;
  indicative: boolean;
  ruleSetVersion: number | null;
  validUntil: string | null;
  decision: string;
  catalogVersion: string | null;
  options: OptionDetail[];
};
export type Loss = { reason: string; note: string | null; at: string; by: string };
export type ProposalSent = {
  id: string;
  version: number;
  options: { carrier: string; option: string }[];
  channel: string;
  sentAt: string;
  expiresAt: string;
};
/** GET /v1/quotes/:id/options */
export type QuoteOptionsView = {
  quoteId: string;
  number: number;
  version: number;
  versions: number;
  status: string;
  validUntil: string | null;
  lost: Loss | null;
  client: { name: string; partyId: string | null } | null;
  carriers: CarrierDetail[];
  proposalChannels: string[];
  proposals: ProposalSent[];
  carrierDocument: { scheduleId: string } | null;
};
type Cell = Pick<CoverageView, 'limit' | 'limitText' | 'deductible' | 'simulated'> | null;
/** GET /v1/quotes/:id/comparison */
export type Comparison = {
  columns: {
    carrier: string;
    name: string;
    option: string;
    planName: string;
    carrierQuoteRef: string | null;
    firm: boolean;
    indicative: boolean;
    validUntil: string | null;
    premium: Premium | null;
  }[];
  rows: { code: string; name: string; cells: Cell[] }[];
  validUntil: string | null;
};
/** GET /v1/quotes/:id/versions/compare */
export type VersionComparison = {
  from: number;
  to: number;
  reason: string | null;
  changes: { path: string; before: string | null; after: string | null }[];
  premiums: {
    carrier: string;
    name: string;
    option: string;
    before: Money | null;
    after: Money | null;
  }[];
};
/** One row of «Cotizaciones» (GET /v1/quotes). */
export type QuoteRow = {
  id: string;
  number?: number | null;
  label: string;
  agentId: string | null;
  partyId?: string | null;
  productLine?: string;
  versions: number;
  updatedAt: string;
  bestPremium: Money | null;
  binding: { policyNumber: string; carrier: string; option: string } | null;
  status?: string;
  validUntil?: string | null;
  origin?: string | null;
  holderName?: string | null;
  carriers?: string[];
  lost?: Loss | null;
};
export type QuoteFilter = {
  q: string;
  status: string;
  productLine: string;
  carrier: string;
  agentId: string;
};
export const NO_FILTER: QuoteFilter = {
  q: '',
  status: '',
  productLine: '',
  carrier: '',
  agentId: '',
};

/** A quote's status as a pill: the text says it (never the color alone). */
export const QUOTE_STATUS: Record<string, [label: string, pill: string]> = {
  OPEN: ['Abierta', 'info'],
  EXPIRED: ['Vencida', 'warn'],
  ISSUED: ['Emitida', 'ok'],
  LOST: ['Perdida', 'bad'],
};
export const QUOTE_ORIGIN: Record<string, string> = {
  RENEWAL: 'Renovación',
  LEAD: 'Prospecto',
  CUSTOMER: 'Cliente',
};
/** Why a quote was lost, as the agent chooses it (and Estadísticas counts it). */
export const QUOTE_LOST_REASON: Record<string, string> = {
  PRICE: 'Precio',
  COVERAGE: 'Cobertura',
  OTHER_CARRIER: 'Eligió otra aseguradora',
  NO_RESPONSE: 'Sin respuesta',
  OTHER: 'Otro',
};
export const CHANNEL_LABEL: Record<string, string> = { WHATSAPP: 'WhatsApp', EMAIL: 'correo' };

/** "gyt" + "ATR" → the key a picked option goes by. */
export const optionKey = (carrier: string, option: string) => `${carrier}:${option}`;

export function statusPill(status: string | undefined, policyNumber?: string | null) {
  const [label, kind] = QUOTE_STATUS[status ?? 'OPEN'] ?? [status ?? '', 'info'];
  return pill(kind, status === 'ISSUED' && policyNumber ? `${label} · ${policyNumber}` : label);
}

const SIMULATED_CHIP =
  ' <span class="chip" title="La aseguradora no publica esta cifra: es un parámetro del simulador, por confirmar">simulado</span>';

/** The sum insured or limit: the amount quoted, or what the catalog says it is. */
const limitOf = (c: Exclude<Cell, null>) =>
  c.limit ? fmt(c.limit) : c.limitText ? esc(c.limitText) : '<span class="muted">Incluida</span>';
const deductibleOf = (c: Exclude<Cell, null>) =>
  c.deductible ? esc(c.deductible) : '<span class="muted">No lo publica</span>';

/** The premium broken down and its installments (the client's money: never the commission). */
export function premiumBreakdown(p: Premium) {
  const rows: [string, string][] = [
    ['Prima neta', fmt(p.net)],
    ['Gastos de emisión', fmt(p.issuanceFee)],
    ['Recargo por fraccionamiento', fmt(p.installmentSurcharge)],
    ['IVA', fmt(p.vat)],
  ];
  const plan =
    p.installments.count === 1
      ? `Un pago de ${fmt(p.installments.first)}`
      : `${p.installments.count} cuotas: primera ${fmt(p.installments.first)}, ${p.installments.count > 2 ? `${p.installments.count - 2} de ${fmt(p.installments.regular)} y ` : ''}última ${fmt(p.installments.last)}`;
  return `<dl class="premium-breakdown">${rows
    .map(([k, v]) => `<div><dt>${k}</dt><dd class="num">${v}</dd></div>`)
    .join(
      '',
    )}<div class="total"><dt>Prima total</dt><dd class="num">${fmt(p.gross)}</dd></div></dl><p class="small muted installments-plan">${plan}</p>`;
}

/** The carrier's facts of its answer: its reference, firm or indicative, tariff, validity. */
export function carrierFacts(c: CarrierDetail) {
  const facts = [
    c.carrierQuoteRef ? `Referencia ${esc(c.carrierQuoteRef)}` : null,
    c.indicative
      ? `Precio indicativo (reglas versión ${esc(c.ruleSetVersion ?? '—')})`
      : c.firm
        ? 'Tarifa en firme'
        : 'Tarifa de la aseguradora',
    c.tariffVersion ? `tarifa ${esc(c.tariffVersion)}` : null,
    c.validUntil ? `válida hasta el ${esc(date(c.validUntil))}` : null,
  ].filter(Boolean);
  return `<p class="small muted carrier-facts">${facts.join(' · ')}</p>`;
}

/** «Coberturas y desglose» of one option: its coverages, its premium, the carrier's facts. */
export function optionDetail(c: CarrierDetail, o: OptionDetail) {
  const simulated = o.coverages.some((x) => x.simulated);
  const coverages = o.coverages.length
    ? `<div class="table-wrap" data-component="table"><table class="table coverages"><thead><tr><th>Cobertura</th><th class="num">Suma asegurada o límite</th><th>Deducible</th></tr></thead><tbody>${o.coverages
        .map(
          (x) =>
            `<tr data-coverage="${esc(x.carrierCode)}"><td>${esc(x.name)}${x.simulated ? SIMULATED_CHIP : ''}</td><td class="num">${limitOf(x)}</td><td>${deductibleOf(x)}</td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : '<p class="muted small">La aseguradora no detalló coberturas para esta opción.</p>';
  return `<details class="option-detail" data-option-detail="${esc(optionKey(c.carrier, o.option))}"><summary>Coberturas y desglose</summary>
    <div class="option-detail-body">${coverages}${o.premium ? premiumBreakdown(o.premium) : ''}${carrierFacts(c)}${simulated ? '<p class="muted small">«simulado»: la aseguradora no publica esa cifra; la confirma al emitir.</p>' : ''}</div></details>`;
}

/** The options side by side: coverages in rows, options in columns, premiums at the foot. */
export function comparisonTable(c: Comparison) {
  const head = c.columns
    .map(
      (col) =>
        `<th scope="col"><span class="compare-carrier">${esc(col.name)}</span><span class="compare-plan">${esc(col.planName)}</span>${col.indicative ? '<span class="badge indicative">Indicativa</span>' : ''}</th>`,
    )
    .join('');
  const rows = c.rows
    .map(
      (r) =>
        `<tr><th scope="row">${esc(r.name)}</th>${r.cells
          .map((x) =>
            x
              ? `<td><strong>${limitOf(x)}</strong>${x.simulated ? SIMULATED_CHIP : ''}<div class="small muted">Deducible: ${deductibleOf(x)}</div></td>`
              : '<td class="muted">No incluida</td>',
          )
          .join('')}</tr>`,
    )
    .join('');
  const money = (label: string, get: (p: Premium) => Money, strong = false) =>
    `<tr class="${strong ? 'total' : ''}"><th scope="row">${label}</th>${c.columns
      .map((col) => `<td class="money">${col.premium ? fmt(get(col.premium)) : '—'}</td>`)
      .join('')}</tr>`;
  const foot = [
    money('Prima neta', (p) => p.net),
    money('Gastos de emisión', (p) => p.issuanceFee),
    money('Recargo por fraccionamiento', (p) => p.installmentSurcharge),
    money('IVA', (p) => p.vat),
    money('Prima total', (p) => p.gross, true),
    `<tr><th scope="row">Cuotas</th>${c.columns
      .map(
        (col) =>
          `<td class="money">${col.premium ? `${col.premium.installments.count} × ${fmt(col.premium.installments.regular)}` : '—'}</td>`,
      )
      .join('')}</tr>`,
  ].join('');
  return `<div class="table-wrap" data-component="table"><table class="table comparison" data-component="comparison"><caption class="sr-only">Opciones comparadas</caption><thead><tr><th scope="col">Cobertura</th>${head}</tr></thead><tbody>${rows}</tbody><tfoot>${foot}</tfoot></table></div>
    <p class="small muted">${c.validUntil ? `La propuesta vale hasta el ${esc(date(c.validUntil))}.` : 'Las aseguradoras no dijeron hasta cuándo vale.'}</p>`;
}

/** Where the proposal can go: one button per channel the client consented to, or why not. */
export function proposalActions(v: QuoteOptionsView, picked: number, canPropose: boolean) {
  const none = picked === 0;
  const download = `<button type="button" class="btn" data-proposal-download${none ? ' disabled' : ''}>${icon('download')}Descargar propuesta (PDF)</button>`;
  if (!canPropose || v.status === 'ISSUED' || v.status === 'LOST') return download;
  if (!v.client)
    return `${download}<p class="note small">${icon('lock')}<span>La cotización no tiene cliente: descarga la propuesta y entrégala tú.</span></p>`;
  if (!v.proposalChannels.length)
    return `${download}<p class="note small">${icon('lock')}<span>${esc(v.client.name)} no ha aceptado mensajes por WhatsApp ni por correo en su ficha: descarga la propuesta y entrégala tú.</span></p>`;
  return `${download}${v.proposalChannels
    .map(
      (ch) =>
        `<button type="button" class="btn primary" data-proposal-send="${esc(ch)}"${none ? ' disabled' : ''}>${icon('bell')}Enviar por ${esc(CHANNEL_LABEL[ch] ?? ch)}</button>`,
    )
    .join('')}`;
}

/** The proposals already sent (never their link). */
export function proposalsSent(v: QuoteOptionsView) {
  if (!v.proposals.length) return '';
  return `<ul class="proposals-sent small">${v.proposals
    .map(
      (p) =>
        `<li>${icon('bell')}Propuesta de la versión ${p.version} con ${p.options.length === 1 ? '1 opción' : `${p.options.length} opciones`}, enviada por ${esc(CHANNEL_LABEL[p.channel] ?? p.channel)} el ${esc(when(p.sentAt))}; su enlace abre hasta el ${esc(when(p.expiresAt))}.</li>`,
    )
    .join('')}</ul>`;
}

/** Closing as lost: the reason, a note (required with «Otro») and the consequence said. */
export function lostForm() {
  return `<form id="quote-lost-form" class="quote-lost" data-component="quote-lost" novalidate>
    <h3>Cerrar como perdida</h3>
    <p class="small muted">La cotización ya no se recotiza ni se emite, y Estadísticas cuenta el motivo. No se deshace.</p>
    <div class="toolbar">
      ${field(
        'Motivo',
        selectControl(
          { id: 'ql-reason' },
          Object.entries(QUOTE_LOST_REASON).map(([value, label]) => ({ value, label })),
        ),
      )}
      <div class="grow">${field('Nota (obligatoria con «Otro»)', textControl({ id: 'ql-note', maxlength: 500, placeholder: 'Eligió quedarse con su aseguradora actual' }))}</div>
      <button type="submit" class="btn danger" id="ql-submit">Cerrar como perdida</button>
    </div></form>`;
}

/** A lost quote says why, when and who. */
export function lostFact(l: Loss) {
  return `<p class="note bad" data-component="quote-lost-fact">${icon('alert')}<span>Perdida por <strong>${esc(QUOTE_LOST_REASON[l.reason] ?? l.reason)}</strong>${l.note ? ` · ${esc(l.note)}` : ''} <span class="muted small">(${esc(when(l.at))})</span></span></p>`;
}

/** "vehicle.insuredValue.amountMinor" → "Valor asegurado"; money in minor units as money. */
const FIELD: Record<string, string> = {
  'vehicle.insuredValue.amountMinor': 'Valor asegurado',
  'vehicle.make': 'Marca',
  'vehicle.model': 'Línea',
  'vehicle.modelYear': 'Modelo (año)',
  'vehicle.use': 'Uso',
  'vehicle.hasTracker': 'GPS',
  paymentFrequency: 'Forma de pago',
  effectiveDate: 'Inicio de vigencia',
};
const valueOf = (path: string, v: string | null) =>
  v === null
    ? '<span class="muted">—</span>'
    : path.endsWith('amountMinor') && /^-?\d+$/.test(v)
      ? q(Number(v))
      : esc(v);

/** What changed from one version to the other: the data asked and each option's premium. */
export function versionDiff(d: VersionComparison) {
  const changes = d.changes.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Dato</th><th>Versión ${d.from}</th><th>Versión ${d.to}</th></tr></thead><tbody>${d.changes
        .map(
          (c) =>
            `<tr><td>${esc(FIELD[c.path] ?? c.path)}</td><td>${valueOf(c.path, c.before)}</td><td>${valueOf(c.path, c.after)}</td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : '<p class="muted small">Los datos del riesgo son los mismos.</p>';
  const premiums = d.premiums.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Opción</th><th class="num">Versión ${d.from}</th><th class="num">Versión ${d.to}</th><th class="num">Diferencia</th></tr></thead><tbody>${d.premiums
        .map((p) => {
          const delta = p.before && p.after ? p.after.amountMinor - p.before.amountMinor : null;
          return `<tr><td>${esc(p.name)} · ${esc(p.option)}</td><td class="num">${fmt(p.before)}</td><td class="num">${fmt(p.after)}</td><td class="num">${delta === null ? '—' : delta === 0 ? 'igual' : `${delta > 0 ? '+' : '−'}${q(Math.abs(delta), p.after?.currency)}`}</td></tr>`;
        })
        .join('')}</tbody></table></div>`
    : '';
  return `<div class="version-diff" data-component="version-diff"><h3>Qué cambió de la versión ${d.from} a la ${d.to}</h3>${d.reason ? `<p class="small">Motivo: ${esc(d.reason)}</p>` : ''}${changes}${premiums}</div>`;
}

/**
 * «Comparar y proponer»: what is picked, the comparison, the proposal's actions, what was sent,
 * the carrier's own PDF once issued, the version comparison and closing as lost.
 */
export function quoteTools(
  v: QuoteOptionsView,
  s: {
    picked: string[];
    comparison: Comparison | null;
    diff: VersionComparison | null;
    canPropose: boolean;
    canClose: boolean;
  },
) {
  const picked = s.picked.length;
  const pickedNote = picked
    ? `${picked === 1 ? '1 opción elegida' : `${picked} opciones elegidas`}.`
    : 'Elige de 1 a 4 opciones con «Comparar» en sus filas.';
  const carrierDoc = v.carrierDocument
    ? `<p class="small">${icon('file')}<button type="button" class="btn sm" data-carrier-quote-doc="${esc(v.carrierDocument.scheduleId)}">PDF de cotización de la aseguradora</button></p>`
    : '';
  const versions =
    v.versions > 1
      ? `<div class="toolbar"><button type="button" class="btn" data-version-compare="${v.version}">Comparar con la versión ${v.version - 1}</button></div>${s.diff ? versionDiff(s.diff) : ''}`
      : '';
  const closing = v.lost
    ? lostFact(v.lost)
    : s.canClose && (v.status === 'OPEN' || v.status === 'EXPIRED')
      ? lostForm()
      : '';
  return `<section class="card quote-tools" data-component="quote-tools" aria-labelledby="quote-tools-title">
    <div class="card-head"><h2 id="quote-tools-title">Comparar y proponer</h2><span class="spacer"></span>${statusPill(v.status)}</div>
    <p class="small muted">${pickedNote}${v.validUntil ? ` La cotización vale hasta el ${esc(date(v.validUntil))}.` : ''}</p>
    <div class="toolbar"><button type="button" class="btn" data-quote-compare${picked ? '' : ' disabled'}>Comparar lado a lado</button>${proposalActions(v, picked, s.canPropose)}</div>
    ${s.comparison ? comparisonTable(s.comparison) : ''}
    ${proposalsSent(v)}${carrierDoc}${versions}${closing}</section>`;
}

/** The search and filters above «Cotizaciones». */
export function quoteFilters(
  f: QuoteFilter,
  o: { carriers: { id: string; name: string }[]; lines: string[]; agents: string[] | null },
) {
  const filtering = Object.values(f).some((x) => !!x);
  const opt = (value: string, label: string, chosen: string) => ({
    value,
    label,
    selected: value === chosen,
  });
  return `<form id="quote-search-form" class="toolbar quote-filters" role="search" aria-label="Buscar cotizaciones" data-component="quote-filters">
    <div class="field grow">${textControl({ label: 'Buscar por número, cliente o DPI', id: 'qf-q', type: 'search', maxlength: 80, autocomplete: 'off', value: f.q, placeholder: 'COT-000123 · Ramírez · 3012456780101' })}</div>
    ${field(
      'Estado',
      selectControl({ id: 'qf-status' }, [
        opt('', 'Todos', f.status),
        ...Object.entries(QUOTE_STATUS).map(([k, [label]]) => opt(k, label, f.status)),
      ]),
    )}
    ${field(
      'Ramo',
      selectControl({ id: 'qf-line' }, [
        opt('', 'Todos', f.productLine),
        ...o.lines.map((l) => opt(l, PRODUCT[l] ?? l, f.productLine)),
      ]),
    )}
    ${field(
      'Aseguradora',
      selectControl({ id: 'qf-carrier' }, [
        opt('', 'Todas', f.carrier),
        ...o.carriers.map((c) => opt(c.id, c.name, f.carrier)),
      ]),
    )}
    ${
      o.agents
        ? field(
            'Agente',
            selectControl({ id: 'qf-agent' }, [
              opt('', 'Todos', f.agentId),
              ...o.agents.map((a) => opt(a, agentName(a), f.agentId)),
            ]),
          )
        : ''
    }
    <div class="order-buttons"><button class="btn primary" type="submit" id="qf-search">Buscar</button>${filtering ? '<button type="button" class="btn" data-quote-filter-clear>Ver todas</button>' : ''}</div></form>`;
}

/** «Cotizaciones»: each with its client, origin, status, validity and best premium. */
export function quoteList(
  rows: QuoteRow[],
  more: boolean,
  f: QuoteFilter,
  o: Parameters<typeof quoteFilters>[1],
) {
  const filtering = Object.values(f).some((x) => !!x);
  const table = rows.length
    ? `<div class="table-wrap" data-component="table"><table class="table quote-list">
      <thead><tr><th>Riesgo</th><th>Cliente</th><th>Origen</th><th>Agente</th><th>Estado</th><th>Válida hasta</th><th class="num">Mejor prima</th><th>Actualizada</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${rows
        .map(
          (
            r,
          ) => `<tr><td><strong>${esc(r.label)}</strong><div class="muted small">${quoteRef(r)}${r.productLine ? ` · ${esc(lineName(r.productLine))}` : ''}${r.versions > 1 ? ` · ${r.versions} versiones` : ''}</div></td>
          <td>${r.holderName ? esc(r.holderName) : '<span class="muted">Sin contratante</span>'}</td>
          <td>${r.origin ? esc(QUOTE_ORIGIN[r.origin] ?? r.origin) : '<span class="muted">Directa</span>'}</td>
          <td>${esc(r.agentId ? agentName(r.agentId) : 'Agencia')}</td>
          <td>${statusPill(r.status ?? (r.binding ? 'ISSUED' : 'OPEN'), r.binding?.policyNumber)}${r.lost ? `<div class="muted small">${esc(QUOTE_LOST_REASON[r.lost.reason] ?? r.lost.reason)}</div>` : ''}</td>
          <td class="nowrap">${r.validUntil ? esc(date(r.validUntil)) : '<span class="muted">—</span>'}</td>
          <td class="num">${fmt(r.bestPremium)}</td>
          <td class="nowrap">${esc(when(r.updatedAt))}</td>
          <td class="actions">${r.partyId ? `<button type="button" class="btn sm" data-party-open="${esc(r.partyId)}">Cliente</button> ` : ''}<button type="button" class="btn sm" data-open-quote="${esc(r.id)}">Abrir</button></td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : `<p class="muted quote-none">${filtering ? 'Ninguna cotización coincide con la búsqueda. Prueba con otro número, nombre o filtro.' : 'Todavía no hay cotizaciones guardadas.'}</p>`;
  return `<section class="card quotes-list" data-component="quotes-list"><div class="card-head"><h2>Cotizaciones</h2></div>
    ${quoteFilters(f, o)}${table}${loadMore('cotizaciones', more, rows.length, 'cotizaciones')}</section>`;
}

/** The query string of the list's filters (only those set). */
export function filterQuery(f: QuoteFilter) {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.status) p.set('status', f.status);
  if (f.productLine) p.set('productLine', f.productLine);
  if (f.carrier) p.set('carrier', f.carrier);
  if (f.agentId) p.set('agentId', f.agentId);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** The carrier's display name for a filter option. */
export const carrierOption = (id: string) => ({ id, name: carrierName(id) });
